import { useMutation } from '@tanstack/react-query';
import * as Api from '@ViewModels';
import { useAdminUserSession } from '../../../models/hooks/adminStateHooks';
import { invalidateBrowseAllSurveyTemplatesQuery } from '../useBrowseAllSurveyTemplatesQuery';
import { invalidateBrowseSurveyTemplatesQuery } from '../useBrowseSurveyTemplatesQuery';
import { invalidateSearchSurveyTemplatesQuery } from '../useSearchSurveyTemplatesQuery';
import { invalidateSurveyTemplateQuery } from '../useSurveyTemplateQuery';
import { invalidateSurveyTemplatesByIndustryQuery } from '../useSurveyTemplatesByIndustryQuery';
import { invalidateSystemSurveyTemplateQuery } from '../useSystemSurveyTemplateQuery';

export interface IPublishSurveyTemplateParams {
	id: string;
	industry: string;
}

export interface IPublishSurveyTemplateOptions {
	onError?: (error: Api.IOperationResultNoValue) => void;
	onSuccess?: () => void;
}

/**
 * Admin: Publish a survey template for a specific industry.
 * Changes the template status to Published for the given industry.
 *
 * @param options - Mutation options including success/error callbacks
 * @returns React Query mutation result
 */
export const usePublishSurveyTemplateMutation = (options: IPublishSurveyTemplateOptions = {}) => {
	const { onError, onSuccess } = options;
	const userSession = useAdminUserSession();

	return useMutation({
		mutationFn: (params: IPublishSurveyTemplateParams) => {
			const { id, industry } = params;

			return userSession.webServiceHelper.callAsync<void>(
				Api.ImpersonationBroker.composeApiUrl({
					urlPath: `admin/surveytemplate/${id}/publish?industry=${encodeURIComponent(industry)}`,
				}),
				'PUT'
			);
		},
		onSuccess: (_data, variables) => {
			// Publishing changes the template's per-industry status — refresh the detail (admin
			// editor + preview) and the lists. (Previous hand-rolled keys matched no query — #9442.)
			invalidateSystemSurveyTemplateQuery(variables.id);
			invalidateSurveyTemplateQuery(variables.id);
			invalidateSurveyTemplatesByIndustryQuery(variables.industry);
			invalidateBrowseAllSurveyTemplatesQuery();
			invalidateBrowseSurveyTemplatesQuery();
			invalidateSearchSurveyTemplatesQuery();
			onSuccess?.();
		},
		onError,
	});
};
